/**
 * Flex `gap` only exists from Chrome 84 / Safari 14.1. Older engines ignore it
 * and the children of a `$flex $gap` Div sit flush against each other - the
 * merchant tablets on Android WebView 81 showed the tracker with every gap
 * gone. A page that has to support them calls `markMissingFlexGap` before its
 * first render, and from then on `Div` spaces its children with margins
 * instead. Until that call finds a browser without flex gap nothing here emits
 * any CSS, so modern browsers and the apps that never call it are untouched.
 *
 * Margins are an approximation: a child's own margin along the main axis is
 * replaced by the gap instead of added to it, and a wrapping row indents the
 * first item of every row after the first.
 *
 * The rules target `&` directly. styled-components 5 resolves the `&` in
 * `.no-flex-gap &` to the component id every Div shares, so an ancestor-class
 * selector would apply every Div's gap to every other Div's children.
 */

/** Set on `<html>` too, for hand-written flex gaps outside `Div`. */
export const NO_FLEX_GAP_CLASS = 'no-flex-gap';

let flexGapMissing = false;

type Direction = 'row' | 'column' | 'column-reverse';

/** The margin that stands in for the gap before each child after the first. */
const LEADING_MARGIN: Record<Direction, string> = {
  row: 'margin-left',
  column: 'margin-top',
  'column-reverse': 'margin-bottom',
};

type FlexGapProps = {
  $flex?: boolean;
  $flexSM?: boolean;
  $grid?: boolean;
  $gap?: string;
  $gapLG?: string;
  $gapMD?: string;
  $gapTablet?: string;
  $gapSM?: string;
  $column?: boolean;
  $columnMD?: boolean;
  $reverseMD?: boolean;
  $columnTablet?: boolean;
  $columnSM?: boolean;
  $reverseSM?: boolean;
  $rowSM?: boolean;
};

type Layout = { flex: boolean; direction: Direction; gap?: string };

/** One breakpoint's margins, undoing what the breakpoint above it set. */
const marginsFor = (layout: Layout, inherited?: Layout) => {
  const active = layout.flex && layout.gap ? layout : undefined;
  const previous =
    inherited?.flex && inherited.gap ? LEADING_MARGIN[inherited.direction] : '';
  const current = active ? LEADING_MARGIN[active.direction] : '';

  const declarations = [
    previous && previous !== current ? `${previous}: 0;` : '',
    active ? `${current}: ${active.gap};` : '',
  ].join('');
  if (!declarations) return '';

  return `& > * + * { ${declarations} }`;
};

const same = (a: Layout, b: Layout) =>
  a.flex === b.flex && a.direction === b.direction && a.gap === b.gap;

/**
 * The margin fallback for a `Div`, mirroring how its own breakpoints resolve
 * `display`, `flex-direction` and `gap` (LG is min-width 3500px; MD, Tablet
 * and SM are nested max-widths, each inheriting from the one above).
 */
export const flexGapFallback = (props: FlexGapProps) => {
  if (!flexGapMissing || props.$grid) return '';

  const base: Layout = {
    flex: Boolean(props.$flex),
    direction: props.$column ? 'column' : 'row',
    gap: props.$flex ? props.$gap : undefined,
  };
  const lg: Layout = { ...base, gap: props.$gapLG || base.gap };
  const md: Layout = {
    ...base,
    gap: props.$gapMD || base.gap,
    direction: props.$reverseMD
      ? 'column-reverse'
      : props.$columnMD
      ? 'column'
      : base.direction,
  };
  const tablet: Layout = {
    ...md,
    gap: props.$gapTablet || md.gap,
    direction: props.$columnTablet ? 'column' : md.direction,
  };
  const sm: Layout = {
    flex: tablet.flex || Boolean(props.$flexSM),
    gap: props.$gapSM || tablet.gap,
    direction: props.$rowSM
      ? 'row'
      : props.$reverseSM
      ? 'column-reverse'
      : props.$columnSM
      ? 'column'
      : tablet.direction,
  };

  const media = (query: string, layout: Layout, inherited: Layout) => {
    const rule = same(layout, inherited) ? '' : marginsFor(layout, inherited);
    return rule ? `@media ${query} { ${rule} }` : '';
  };

  return [
    marginsFor(base),
    media('(min-width: 3500px)', lg, base),
    media('(max-width: 1450px)', md, base),
    media('(max-width: 1100px)', tablet, md),
    media('(max-width: 800px)', sm, tablet),
  ].join('\n');
};

/**
 * Turns the fallback on (and tags `<html>` with `no-flex-gap`) when the
 * browser lays out a flex column without its gap. Call once, before the first
 * render of a page that has to support old WebViews: styled-components only
 * picks the flag up for Divs rendered after it.
 */
export const markMissingFlexGap = () => {
  if (typeof document === 'undefined') return;
  try {
    const probe = document.createElement('div');
    probe.style.cssText =
      'display:flex;flex-direction:column;row-gap:1px;position:absolute;visibility:hidden';
    probe.appendChild(document.createElement('div'));
    probe.appendChild(document.createElement('div'));
    document.documentElement.appendChild(probe);
    const supported = probe.scrollHeight === 1;
    probe.remove();
    if (!supported) {
      flexGapMissing = true;
      document.documentElement.classList.add(NO_FLEX_GAP_CLASS);
    }
  } catch {
    // Leave the page as it is rather than break it over a probe.
  }
};
